import React, { useState } from "react";
import LoadingComponent from "../../components/common/LoadingComponent";
import PlatformDropdown from "../Conversation/PlatformDropdown";
import { Icons } from "../../constants/icon-library";
import { resolveMentions } from "../Conversation/Conversation.utils";
import { confirmationModal } from "../../constants/confirmation.constants";
import { TaskOptionsDropdown } from "./Task.utils";
import { useConversationContext } from "../Conversation/ConversationContext";

function TaskItem({
  taskItem,
  taskHandlers,
  replyProps,
  platformProps,
  members = [],
  onAssignMembers,
  canReply = true,
}) {
  const { authorMap } = useConversationContext();
  const { onRemove } = taskHandlers;
  const {
    replyOpen,
    replyDraft,
    onToggleReply,
    onReplyChange,
    onSendReply,
    isSendingReply,
  } = replyProps;
  const {
    platforms,
    platformMenuOpen,
    onTogglePlatformMenu,
    onSelectPlatform,
  } = platformProps;

  const [showMembers, setShowMembers] = useState(false);

  const isCompleted = taskItem.status === "completed";

  const memberOptions = members.map((member) => ({
    value: member.uuid,
    label: member.name || member.email,
  }));

  const selectedMembers = taskItem.members || [];

  const handleDeleteClick = async () => {
    const result = await confirmationModal({
      title: "Delete this task?",
      text: `"${taskItem.name || "This task"}" will be permanently removed.`,
      confirmText: "Delete",
      cancelText: "Cancel",
      isDanger: true,
    });

    if (result.isConfirmed) {
      onRemove(taskItem);
    }
  };

  return (
    <div className="bg-theme shadow-sm px-3 pt-3 pb-3 conv-task-card">
      <div className="position-relative pb-4">
        <span
          className={`badge border-0 conv-task-status-badge ${
            isCompleted
              ? "bg-success-subtle text-success-emphasis"
              : "bg-warning-subtle text-warning-emphasis"
          }`}
          title={isCompleted ? "Completed" : "Pending"}
        >
          {isCompleted ? "Completed" : "Pending"}
        </span>

        <div className="conv-task-content">
          <div className="d-flex align-items-baseline gap-2 flex-wrap">
            <span className="fw-semibold conv-task-name">
              {resolveMentions(taskItem.name, authorMap)}
            </span>
          </div>
          <div className="text-body conv-task-desc">
            {resolveMentions(taskItem.description, authorMap)}
          </div>

          {taskItem.replies?.length > 0 && (
            <div className="mt-2 d-flex flex-column gap-1">
              {taskItem.replies.map((r, i) => (
                <div
                  key={i}
                  className="text-body-secondary conv-task-reply-item"
                >
                  {resolveMentions(r.text, authorMap)}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="d-flex flex-row align-items-center gap-1 conv-task-actions">
          <button
            type="button"
            className="btn btn-sm btn-link text-body-secondary p-1"
            title={canReply ? "Reply to task" : "Cannot reply to specific messages in this platform"}
            onClick={() => onToggleReply(taskItem.uuid)}
            disabled={!canReply}
          >
            <i className="bi bi-reply" />
          </button>

          <div style={{ position: "relative" }}>
            <button
              type="button"
              className="btn btn-sm btn-link text-body-secondary p-1"
              title={
                isCompleted ? "Task already completed" : "Execute on platform"
              }
              onClick={onTogglePlatformMenu}
              disabled={isCompleted}
            >
              <i className={`wb ${Icons("resume")} align-middle ps-1 py-1`} />
            </button>
            {platformMenuOpen && !isCompleted && (
              <PlatformDropdown
                platforms={platforms}
                onSelect={onSelectPlatform}
                onClose={onTogglePlatformMenu}
              />
            )}
          </div>

          <div style={{ position: "relative" }}>
            <button
              type="button"
              className="btn btn-sm btn-link text-body-secondary p-1"
              title="Assign to member"
              onClick={() => setShowMembers((prev) => !prev)}
            >
              <i className="wb webconnect-person-plus align-middle ps-1 py-1" />
            </button>

            {showMembers && (
              <div className="member-popover">
                <div className="member-popover-header">
                  <span>Members</span>
                  <button
                    type="button"
                    className="member-popover-close"
                    onClick={() => setShowMembers(false)}
                  >
                    <i className="bi bi-x-lg" />
                  </button>
                </div>
                <TaskOptionsDropdown
                  options={memberOptions}
                  value={selectedMembers}
                  requireOptionFormat={false}
                  onChange={(values) =>
                    onAssignMembers && onAssignMembers(taskItem, values)
                  }
                  type="multiselect"
                />
              </div>
            )}
          </div>

          <button
            type="button"
            className="btn btn-sm btn-link text-body-secondary p-1"
            title="Remove task"
            onClick={handleDeleteClick}
          >
            <i className="bi bi-trash" />
          </button>
        </div>
      </div>

      {replyOpen && (
        <div className="d-flex gap-2 mt-1 conv-task-reply-row">
          <input
            type="text"
            className="form-control form-control-sm bg-body border-0 rounded-pill px-3 conv-task-reply-input"
            placeholder="Write a reply..."
            value={replyDraft || ""}
            onChange={(e) => onReplyChange(taskItem.uuid, e.target.value)}
            disabled={isSendingReply}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                onSendReply(taskItem);
              }
            }}
          />
          <button
            type="button"
            className="btn btn-sm btn-primary rounded-pill px-3 flex-shrink-0 d-flex align-items-center justify-content-center conv-task-reply-send"
            onClick={() => onSendReply(taskItem)}
            disabled={!replyDraft?.trim() || isSendingReply}
          >
            {isSendingReply ? (
              <span className="conv-spinner-wrap">
                <LoadingComponent
                  loading={true}
                  size={6}
                  wrapperStyle={{
                    padding: 0,
                    lineHeight: 0,
                    display: "flex",
                    flexDirection: "row",
                    whiteSpace: "nowrap",
                  }}
                />
              </span>
            ) : (
              <i className="bi bi-send-fill" />
            )}
          </button>
        </div>
      )}
    </div>
  );
}

export default TaskItem;